/**
 * `hashSessionKey` — deterministic session-key derivation for the docs slider
 * and chatbot dock (Algorithm F1 in `design.md`).
 *
 * @remarks
 * The chatbot transport MUST NOT see the raw `ownerId` — it receives a hashed,
 * truncated identifier instead (design § Overview principle 4, § Function F1,
 * property P5). This module centralises that hash so every caller derives the
 * key the same way.
 *
 * ### Why Web Crypto instead of Node's `crypto` module
 *
 * The earlier implementation used Node's built-in `crypto` module (via
 * `import { createHash } from 'crypto'`). That works under Jest/jsdom because
 * Node exposes the module natively, but CRA5/webpack5 does NOT auto-polyfill
 * Node built-ins, so the production `react-app-rewired build` failed with:
 *
 *     Module not found: Error: Can't resolve 'crypto' in
 *     'frontend/src/help-docs-panel'
 *
 * Rather than pulling in a ~30 KB `crypto-browserify` polyfill, we use the
 * platform-standard Web Crypto API (`crypto.subtle.digest`). It is available
 * in every browser supported by the project's `browserslist` config and
 * exposed globally in Node 20+ (which is why the Jest suites still pass).
 * See `frontend/src/setupTests.ts` for the Node-webcrypto shim that ensures
 * `globalThis.crypto.subtle` is defined in the jsdom test environment.
 *
 * ### API shape
 *
 * The function is asynchronous because `crypto.subtle.digest` returns a
 * `Promise<ArrayBuffer>`. Callers that previously consumed the sync return
 * value now wrap the promise in `useEffect + useState` (see
 * `useHashedSessionKey`), or `await` it in tests.
 *
 * @packageDocumentation
 */

/**
 * Regex matched by every non-error return value of {@link hashSessionKey}.
 * Exported so callers/tests can pin the postcondition without duplicating
 * the pattern. Matches exactly 16 lowercase hexadecimal characters.
 */
export const HASHED_SESSION_KEY_PATTERN = /^[a-f0-9]{16}$/;

/**
 * Derive a stable, tenant-safe session key from an `ownerId`.
 *
 * @param ownerId - The Clerk-org-derived owner id (Constitution Rule II).
 *   MUST be a non-empty string; an empty string yields a rejected promise
 *   with a `TypeError`.
 * @returns A promise resolving to the first 16 lowercase hexadecimal
 *   characters of `SHA-256(ownerId)`. The result matches
 *   {@link HASHED_SESSION_KEY_PATTERN}.
 *
 * @throws {TypeError} (as a rejected promise) When `ownerId` is the empty
 *   string. The TypeScript signature already forbids `null` / `undefined`
 *   at compile time; this runtime guard covers the empty-string case only.
 *
 * @example
 * ```ts
 * await hashSessionKey('abc');            // "ba7816bf8f01cfea"
 * (await hashSessionKey('abc')) === (await hashSessionKey('abc')); // true
 * ```
 */
export async function hashSessionKey(ownerId: string): Promise<string> {
  if (ownerId === '') {
    throw new TypeError('hashSessionKey: ownerId must be a non-empty string');
  }
  const encoded = new TextEncoder().encode(ownerId);
  const digest = await crypto.subtle.digest('SHA-256', encoded);
  const bytes = new Uint8Array(digest);
  // Emit the first 8 bytes as 16 lowercase hex characters. Slicing the
  // hex-encoded 32-byte digest at 16 chars is equivalent; taking the
  // 8-byte prefix directly avoids allocating a 64-char string only to
  // discard 48 of it.
  let hex = '';
  for (let i = 0; i < 8; i += 1) {
    hex += bytes[i].toString(16).padStart(2, '0');
  }
  return hex;
}
